/**
 * @description Code-defined HtmlDocument payloads for public routes whose layout lives
 *              entirely in React and so needs no CMS (DynamoDB `htmlDocumentByAlias`) record.
 *
 *              Each entry has the same shape as a `urlRouteByAlias` response, so it renders
 *              through the normal `HtmlPage` -> `DynamicComponent` path with the standard
 *              site navbar and footer panels. App and the HtmlPage selector consult these
 *              (after the generated `aliasCache`) before fetching from the API, so a route
 *              listed here never issues an `htmlPageQuery`.
 *
 *              Unlike alias-cache.ts this file is hand-maintained - it is not regenerated.
 * @filename static-routes.ts
 */

import { aliasCache } from "./alias-cache";

const staticPanel = (alias: string, key: string, index: number) => ({
  id: `static-${alias}-panel-${index}`,
  component: { key, id: `static-${key}`, properties: null },
  properties: null,
});

const staticDocument = (alias: string, componentKeys: string[]) => ({
  urlRouteByAlias: {
    hasErrors: false,
    errors: null,
    node: {
      id: `static-${alias}`,
      panels: componentKeys.map((key, index) => staticPanel(alias, key, index)),
    },
  },
});

/** Route alias (first path segment) -> HtmlDocument payload. */
export const STATIC_ROUTE_DOCUMENTS: { readonly [alias: string]: any } = {
  advocacy: staticDocument("advocacy", ["TransparentNavbar", "AdvocacyPanel", "Footer"]),
  blog: staticDocument("blog", ["TransparentNavbar", "BlogHomePanel", "Footer"]),
};

/**
 * Returns the built-in HtmlDocument for a page cache key (`${alias}-${queryParameters}`),
 * from the generated prerender alias cache or the code-defined static routes.
 * Query strings (e.g. tracking parameters) do not change which static route is served.
 */
export function getBuiltInHtmlDocument(cacheKey: string): any {
  if (!cacheKey) {
    return undefined;
  }
  if (aliasCache[cacheKey]) {
    return aliasCache[cacheKey];
  }
  const alias = Object.keys(STATIC_ROUTE_DOCUMENTS).find((key) => cacheKey.indexOf(`${key}-`) === 0);
  return alias ? STATIC_ROUTE_DOCUMENTS[alias] : undefined;
}
